import React, { useRef } from "react";
import { useSelector } from "react-redux";

import { DropIndicator } from "app/components/DropIndicator";
import { BoardViewType } from "app/types";
import AddTicket from "../../../../components/AddTicket";
import { shouldShowAddTaskIcon } from "../../../../helpers";
import { selectShowCreateTicketIntoGroup } from "../../../Tickets/redux/selector";

import { TicketGroup } from "../../../Tickets/types";
import AddTaskButton from "./AddTaskButton";
import ColumnLoading from "./ColumnLoading";
import DraggableColumn from "./DraggableColumn";
import GroupHeader from "./GroupHeader";
import { useColumnDrop } from "../../../../components/ticketDragHook";
import clsx from "clsx";

interface ColumnProps {
  group: TicketGroup;
  groupLoading: boolean;
  groupTicketsLoading: boolean;
  renderOnlyHeader?: boolean;
}
function DropGap({ height }: { height: number }) {
  return (
    <div
      className="w-full rounded-lg border-2 border-dashed border-[#E9ECF2] flex-shrink-0"
      style={{ height: height > 0 ? `${height}px` : "120px" }}
    />
  );
}
function ColumnComponent({
  group,
  groupLoading,
  groupTicketsLoading,
  renderOnlyHeader = false,
}: ColumnProps) {
  const dropzoneRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);

  const showAddTaskIcon = useSelector(selectShowCreateTicketIntoGroup);
  const isAddTaskVisible = shouldShowAddTaskIcon(showAddTaskIcon, group._id);

  const { isDraggingOver, dropEdge, draggingCardHeight, closestTicketId } =
    useColumnDrop({
      group,
      dropzoneRef,
    });
  if (renderOnlyHeader) {
    return (
      <div className="flex h-full flex-col">
        <GroupHeader group={group} loading={groupLoading} />
      </div>
    );
  }

  const renderTickets = () =>
    group.ticketsList.map((ticket, index) => {
      const isTrigger =
        index === Math.floor(group?.ticketsList?.length * 0.75) - 1;
      const isClosest = ticket._id === closestTicketId;

      return (
        <React.Fragment key={ticket._id}>
          {isClosest && isDraggingOver && dropEdge === "top" && (
            <DropGap height={draggingCardHeight} />
          )}
          <DraggableColumn ticket={ticket} group={group} />
          {isClosest && isDraggingOver && dropEdge === "bottom" && (
            <DropGap height={draggingCardHeight} />
          )}
          {isTrigger && <div ref={loadMoreTriggerRef} style={{ height: 1 }} />}
        </React.Fragment>
      );
    });

  const renderLoading = () =>
    group?.tickets?.map((id) => <ColumnLoading key={id} />);

  return (
    <div className="flex h-full flex-col">
      <GroupHeader group={group} loading={groupLoading} />

      <div
        ref={(node) => {
          if (node) {
            (dropzoneRef as any).current = node;
            (scrollContainerRef as any).current = node;
          }
        }}
        className={clsx(
          "flex flex-col mt-4 gap-3 h-full overflow-y-auto relative pb-12",
        )}
      >
        {isAddTaskVisible && (
          <AddTicket type={BoardViewType.KANBAN} group={group} />
        )}

        {groupTicketsLoading ? renderLoading() : renderTickets()}
      </div>

      {group.subGroups?.length == 0 ? (
        <div className="fixed bottom-0 pt-2 pb-4 bg-gray-400 w-full z-10">
          <AddTaskButton
            groupId={group._id}
            dropzoneRef={dropzoneRef}
            showAddTaskIcon={isAddTaskVisible}
          />
        </div>
      ) : (
        <> </>
      )}
    </div>
  );
}

export default React.memo(ColumnComponent);
